/**
 * WhatsApp Signup Block Component
 *
 * Shopify's customer form can't store a phone number or WhatsApp consent directly,
 * so on submit the number is normalised to international format and sent as customer tags:
 *   "whatsapp-consent, whatsapp-phone:<digits>, whatsapp-placeholder-email"
 * The form also requires an email, so a placeholder "wa-<digits>@<shop domain>" is filled in.
 * A Shopify Flow workflow reads these tags, sets the customer's WhatsApp marketing consent
 * and unsubscribes the placeholder email from email marketing.
 */
class WhatsAppSignupBlock extends HTMLElement {
  constructor() {
    super();

    this.handleSubmit = this.handleSubmit.bind(this);
    this.clearValidity = this.clearValidity.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.phoneInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.whatsapp-signup__input--phone'));
    this.consentInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.whatsapp-signup__consent-input'));
    this.tagsInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.whatsapp-signup__tags'));
    this.emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.whatsapp-signup__email'));

    this.form?.addEventListener('submit', this.handleSubmit);
    this.phoneInput?.addEventListener('input', this.clearValidity);
    this.consentInput?.addEventListener('change', this.clearValidity);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.phoneInput?.removeEventListener('input', this.clearValidity);
    this.consentInput?.removeEventListener('change', this.clearValidity);
  }

  /**
   * @param {Event} event
   */
  clearValidity(event) {
    /** @type {HTMLInputElement} */ (event.target).setCustomValidity('');
  }

  /**
   * Converts what the customer typed into international digits without "+".
   * "010 1234 5678" with country code 20 -> "201012345678"
   * "+44 7700 900123" or "0044 7700 900123" -> "447700900123"
   * @param {string} value
   * @returns {string | null}
   */
  normalizePhone(value) {
    const trimmed = value.trim();
    let digits = trimmed.replace(/\D/g, '');

    if (trimmed.startsWith('+')) {
      // Already international
    } else if (digits.startsWith('00')) {
      digits = digits.slice(2);
    } else {
      const countryCode = (this.dataset.countryCode || '').replace(/\D/g, '');
      digits = digits.replace(/^0+/, '');
      if (countryCode && !digits.startsWith(countryCode)) digits = countryCode + digits;
    }

    // E.164 numbers are at most 15 digits
    return digits.length >= 8 && digits.length <= 15 ? digits : null;
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    if (!this.phoneInput || !this.tagsInput || !this.emailInput) return;

    const phone = this.normalizePhone(this.phoneInput.value);

    if (!phone) {
      event.preventDefault();
      this.phoneInput.setCustomValidity(this.dataset.phoneError || '');
      this.phoneInput.reportValidity();
      return;
    }

    if (this.consentInput && !this.consentInput.checked) {
      event.preventDefault();
      this.consentInput.setCustomValidity(this.dataset.consentError || '');
      this.consentInput.reportValidity();
      return;
    }

    const extraTags = (this.dataset.extraTags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    this.emailInput.value = `wa-${phone}@${this.dataset.emailDomain}`;
    this.tagsInput.value = ['whatsapp-consent', `whatsapp-phone:${phone}`, 'whatsapp-placeholder-email', ...extraTags].join(
      ', '
    );
  }
}

if (!customElements.get('whatsapp-signup-block')) {
  customElements.define('whatsapp-signup-block', WhatsAppSignupBlock);
}
